import config from "../config";
import { refreshSession } from "./Auth";
import UserPool from "src/auth/UserPool";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import history from "src/redux/history";

// ---- Config ----
const EXPIRY_BUFFER_MS = 60_000; // refresh if <= 60s left
const TOKEN_CHECK_INTERVAL_MS = 30_000; // check every 30s
const IDLE_THRESHOLD_MS = 5 * 60_000; // 5 min idle = inactive

let logoutTimerId: ReturnType<typeof setTimeout> | null = null;
let checkIntervalId: ReturnType<typeof setInterval> | null = null;
let lastActivityMs: number = Date.now();

// ---- Cross-tab coordination via BroadcastChannel ----
const tabChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("session_sync") : null;

type TabMessage =
  | { type: "LOGOUT"; reason: string }
  | { type: "TOKEN_REFRESHED"; expiresAtMs: number };

let refreshInFlight = false;

// Track user activity
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"] as const;
const updateActivity = () => { lastActivityMs = Date.now(); };
ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, updateActivity, { passive: true, capture: true }));

function isUserActive(): boolean {
  return Date.now() - lastActivityMs < IDLE_THRESHOLD_MS;
}

function clearTokenExpiryTimer() {
  if (logoutTimerId) { clearTimeout(logoutTimerId); logoutTimerId = null; }
  if (checkIntervalId) { clearInterval(checkIntervalId); checkIntervalId = null; }
}

// Centralized auto-logout (sign out + redirect back after login)
let logoutInProgress = false;

export function autoLogout(reason: string): void {
  if (logoutInProgress) return;
  logoutInProgress = true;

  clearTokenExpiryTimer();
  tabChannel?.postMessage({ type: "LOGOUT", reason } as TabMessage);
  try {
    localStorage.clear();
    sessionStorage.clear();
    UserPool.getCurrentUser()?.signOut();
  } catch {}

  history.push("/login");
  logoutInProgress = false;
}

async function handleTokenExpiring(expiresAtMs: number) {
  if (refreshInFlight) return;
  refreshInFlight = true;
  try {
    if (isUserActive()) {
      const user = UserPool.getCurrentUser();
      if (!user) { autoLogout("No signed-in user found"); return; }

      const session: any = await new Promise((resolve, reject) => {
        user.getSession((err: any, sess: unknown) =>
          err || !sess ? reject(err ?? new Error("No session")) : resolve(sess)
        );
      });
      const refreshToken = session.getRefreshToken()?.getToken();
      const username = user.getUsername();
      if (!refreshToken || !username) { autoLogout("Missing refresh token or username"); return; }

      const { accessToken } = await refreshSession(refreshToken, username);
      if (!accessToken?.jwtToken) { autoLogout("No jwtToken in refresh response"); return; }

      const newExpMs = accessToken.payload?.exp ? accessToken.payload.exp * 1000 : expiresAtMs;
      scheduleAutoLogout(newExpMs);
      tabChannel?.postMessage({ type: "TOKEN_REFRESHED", expiresAtMs: newExpMs } as TabMessage);
    } else {
      autoLogout("User idle — session expired");
    }
  } catch {
    autoLogout("Refresh failed (idle check)");
  } finally {
    refreshInFlight = false;
  }
}

function scheduleAutoLogout(expiresAtMs: number) {
  clearTokenExpiryTimer();

  const msUntilExpiry = expiresAtMs - Date.now();
  if (msUntilExpiry <= 0) {
    handleTokenExpiring(expiresAtMs);
    return;
  }

  // When token is about to expire, check activity and decide refresh vs logout
  const msUntilCheck = Math.max(msUntilExpiry - EXPIRY_BUFFER_MS, 0);
  logoutTimerId = setTimeout(() => handleTokenExpiring(expiresAtMs), msUntilCheck);

  // Safety net for system sleep/wake + proactive refresh when idle user returns
  let wasIdle = false;
  checkIntervalId = setInterval(() => {
    const now = Date.now();
    const active = isUserActive();

    // If token is within the expiry buffer, handle it
    if (now >= expiresAtMs - EXPIRY_BUFFER_MS) {
      clearInterval(checkIntervalId!);
      checkIntervalId = null;
      handleTokenExpiring(expiresAtMs);
      return;
    }

    // If user was idle and just became active again, refresh proactively
    if (wasIdle && active) {
      clearInterval(checkIntervalId!);
      checkIntervalId = null;
      handleTokenExpiring(expiresAtMs);
      return;
    }

    wasIdle = !active;
  }, TOKEN_CHECK_INTERVAL_MS);
}

/**
 * Returns a **valid** Cognito access token (JWT), or `null` if no user is
 * currently signed in (e.g. on the login page).
 *
 * When an authenticated session exists but cannot be refreshed, we
 * auto-logout so stale credentials never linger.
 */
export async function getCognitoAccessToken(): Promise<string | null> {
  const user = UserPool.getCurrentUser();

  if (!user) return null;

  // ---- Load current session (callback API -> Promise) ----

  let session: any;

  try {
    session = await new Promise((resolve, reject) => {
      user.getSession((err: any, sess: unknown) =>
        err || !sess ? reject(err ?? new Error("No session")) : resolve(sess)
      );
    });
  } catch (error) {
    console.error("Failed to get Cognito session:", error);
    return null;
  }

  // ---- Check access token expiry directly ----

  const access = session.getAccessToken();

  if (!access) {
    autoLogout("Missing access token");
    return null;
  }

  const expMs = access.getExpiration() * 1000;
  const msLeft = expMs - Date.now();

  if (msLeft > EXPIRY_BUFFER_MS) {
    scheduleAutoLogout(expMs);
    return access.getJwtToken();
  }

  // ---- Session needs refresh ----

  const refreshToken = session.getRefreshToken()?.getToken();
  const username = user.getUsername();

  if (!refreshToken || !username) {
    autoLogout("Missing refresh token or username");
    return null;
  }

  try {
    const { accessToken } = await refreshSession(refreshToken, username);

    if (!accessToken?.jwtToken) {
      autoLogout("No jwtToken in refresh response");
      return null;
    }

    const newExpMs = accessToken.payload?.exp ? accessToken.payload.exp * 1000 : expMs;

    scheduleAutoLogout(newExpMs);

    return accessToken.jwtToken;
  } catch (error) {
    console.error("Cognito token refresh failed:", error);

    autoLogout("Refresh failed");
    return null;
  }
}





// Create Axios instance
const apiClient = axios.create({
  baseURL: `${config.API_BASE_HOST}/ui/v1`,
  withCredentials: true,
});
apiClient.defaults.headers.common.Accept = "application/json";

// Interceptor: inject Cognito Access Token into Authorization header
apiClient.interceptors.request.use(
  async (reqConfig: InternalAxiosRequestConfig) => {
    const token = await getCognitoAccessToken();
    reqConfig.headers = reqConfig.headers ?? {};
    if (token) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    return reqConfig;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Interceptor: force logout on 401 response
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      autoLogout("Received 401 from server");
    }
    return Promise.reject(error);
  }
);

// Listen for cross-tab messages
if (tabChannel) {
  tabChannel.onmessage = (event: MessageEvent<TabMessage>) => {
    const msg = event.data;
    if (msg.type === "LOGOUT") {
      clearTokenExpiryTimer();
      try {
        localStorage.clear();
        sessionStorage.clear();
        UserPool.getCurrentUser()?.signOut();
      } catch {}
      history.push("/login");
    } else if (msg.type === "TOKEN_REFRESHED") {
      scheduleAutoLogout(msg.expiresAtMs);
    }
  };
}

export default apiClient;
